import { useEffect, useState, useRef } from 'react';

/** True when the visitor asked for reduced motion. */
export function prefersReduced() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useReducedMotionPref() {
  const [r, setR] = useState(prefersReduced());
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const f = () => setR(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return r;
}

/**
 * Seamless loop clock. Returns t in [0, period).
 * Pauses when document.hidden or when `active` is false (off-screen).
 * window.__T (seconds) overrides the clock for frame-exact capture.
 */
export function useLoop(period, still, active = true) {
  const reduced = useReducedMotionPref();
  const [t, setT] = useState(still);
  const t0Ref = useRef(null);
  const elapsedRef = useRef(still);
  const lastSetRef = useRef(still);

  useEffect(() => {
    if (reduced) {
      setT(still);
      lastSetRef.current = still;
      return undefined;
    }
    if (!active) return undefined;

    let raf = 0;
    // Continue from last elapsed so pause/resume does not hard-reset the loop seam.
    t0Ref.current = performance.now() - elapsedRef.current * 1000;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const now =
        typeof window !== 'undefined' && typeof window.__T === 'number'
          ? window.__T
          : (performance.now() - t0Ref.current) / 1000;
      const next = ((now % period) + period) % period;
      elapsedRef.current = next;
      // Throttle React updates (~30fps) to avoid layout thrash / scroll anchoring.
      if (Math.abs(next - lastSetRef.current) >= 1 / 30 || next < lastSetRef.current) {
        lastSetRef.current = next;
        setT(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [period, still, reduced, active]);

  return t;
}

/** Pause loops when element is off-screen. */
export function useInViewActive(ref, { rootMargin = '80px' } = {}) {
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref && ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setActive(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => setActive(Boolean(entry && entry.isIntersecting)),
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin]);
  return active;
}

export const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
export const prog = (t, a, b) => clamp((t - a) / (b - a));
export const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
export const eout = (x) => 1 - Math.pow(1 - clamp(x), 3);
export const lerp = (a, b, k) => a + (b - a) * k;
